import React, { useState, useEffect } from 'react';
import {
    Form,
    Select,
    DatePicker,
    Input,
    InputNumber,
    Checkbox,
    Button,
    message,
    Card,
    Typography,
    Tooltip,
    Spin,
    Switch
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import helper from '../../utils/helper';
import { request } from '../../../../shared/src/apis';
import {
    ICompanyDropDown,
    IService,
    IOfferFormData,
    IFormState,
} from '../../utils/commonInterfaces';

const { Option } = Select;
const { Title } = Typography;

export const CreateOffer = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [freeMonthsMode, setFreeMonthsMode] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [formState, setFormState] = useState<IFormState>({
        loading: false,
        loadingData: true,
        companies: [],
        services: [],
        errorMode: false,
        errorMessage: null
    });
    //test
    const fetchInitialData = async () => {
        try {
            updateFormState({
                loadingData: true,
                errorMode: false,
                errorMessage: null
            });

            const [companiesRes, servicesRes] = await Promise.all([
                helper.getAllCompanies() as any,
                helper.getServiceTypes() as any
            ]);

            if (companiesRes?.status === 200 && companiesRes?.data) {
                const companiesData = Array.isArray(companiesRes?.data) ? companiesRes?.data : [];
                updateFormState({ companies: companiesData });
            } else {
                const errorMsg = companiesRes?.message || 'Failed to fetch companies';
                message.error(errorMsg);
                updateFormState({
                    errorMode: true,
                    errorMessage: errorMsg
                });
            }

            if (servicesRes?.status === 200 && servicesRes?.data?.data?.servicesGroups) {
                const servicesData = Array.isArray(servicesRes?.data?.data?.servicesGroups)
                    ? servicesRes?.data?.data?.servicesGroups
                    : [];
                updateFormState({ services: servicesData });
            } else {
                const errorMsg = servicesRes?.message || 'Failed to fetch services';
                message.error(errorMsg);
                updateFormState({
                    errorMode: true,
                    errorMessage: errorMsg
                });
            }

        } catch (error: any) {
            const errorMsg = error?.message || 'Failed to fetch required data. Please refresh the page.';
            console.error('Error fetching initial data:', error);
            message.error(errorMsg);
            updateFormState({
                errorMode: true,
                errorMessage: errorMsg
            });
        } finally {
            updateFormState({ loadingData: false });
        }
    };
    useEffect(() => {
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFormState = (updates: Partial<IFormState>) => {
        setFormState(prev => ({ ...prev, ...updates }));
    };


    const onFinishFailed = (errorInfo: any) => {
        console.log('Validation failed:', errorInfo);
    };

    const onFinish = async (values: IOfferFormData) => {

        try {
            updateFormState({ loading: true });

            const submitData: any = {
                companyID: Number(values?.companyID),
                serviceGroupID: values?.services,
                startDate: values?.startDate?.format('YYYY-MM-DD'),
                endDate: values?.endDate?.format('YYYY-MM-DD'),
                maxAmountPerUser: values?.maxAllowedAmount ? Number(values?.maxAllowedAmount) : undefined,
                maxNumberOfRequestsPerUser: values?.maxAllowedRequests ? Number(values?.maxAllowedRequests) : undefined,
                firstTransactionUser: values?.activateUponFirstRequest,
                freePeriodTypeID: freeMonthsMode ? 2 : 1,

            };

            if (freeMonthsMode) {
                submitData.numberOfFreeMonths = Number(values?.freeMonths);
                submitData.companyMaxInstallments = Number(values?.companyMaxInstallments);
                submitData.minNumberOfFreeInstallments = Number(values?.minInstallments);
            } else {
                submitData.discountPercent = Number(values?.discountPercentage);
            }

            const response: any = await dispatch(request({
                url: '/companies/freePeriod',
                method: 'POST',
                data: submitData
            }));

            if (response?.data?.status === 200) {
                message.success('Free period offer created successfully!');
                form.resetFields();
                setWarningMessage('');
            } else {
                const errorMsg = response?.data?.message || 'Failed to create free period offer. Please try again.';
                message.error(errorMsg);
            }

        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create free period offer. Please try again.';
            console.error('Error creating free period offer:', error);
            message.error(errorMsg);
        } finally {
            updateFormState({ loading: false });
        }
    };

    const onReset = () => {
        form.resetFields();
        message.info('Form has been reset');
    };

    const disabledDate = (current: moment.Moment | null) => {
        if (!current) return false;
        return current < moment().startOf('day');
    };

    const dropDownSearch = (input: string, option: any) => {
        if (!input || !option?.children) return false;
        return option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    if (formState.loadingData) {
        return (
            <div className="create-offer-container">
                <Card className="offer-card">
                    <div className="card-header">
                        <Title level={2} className="card-title">
                            Create Free Period Offer
                        </Title>
                        <p className="card-subtitle">Loading required data...</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                    </div>
                </Card>
            </div>
        );
    }

    if (formState.errorMode) {
        return (
            <div className="create-offer-container">
                <Card className="offer-card">
                    <div className="card-header">
                        <Title level={2} className="card-title">
                            Create Free Period Offer
                        </Title>
                        <p className="card-subtitle">Error loading data</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <p style={{ color: '#ff4d4f', marginBottom: '20px' }}>
                            {formState.errorMessage || 'Failed to load required data. Please refresh the page or try again later.'}
                        </p>
                        <Button type="primary" onClick={fetchInitialData}>
                            Retry
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="create-offer-container">
            <Card className="offer-card">
                <div className="card-header">
                    <Title level={2} className="card-title">
                        Create Free Period Offer
                    </Title>
                    <p className="card-subtitle">Fill in the details below to create a new offer</p>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    className="offer-form"
                    initialValues={{
                        activateUponFirstRequest: false,
                        services: formState.services.find(s => s.id === 10)?.id ??
                            formState.services[0]?.id,
                        maxAllowedRequests: 1,
                        maxAllowedAmount: 100000
                    }}
                >
                    <Form.Item
                        label="Free Months Mode"
                    >
                        <Switch
                            checked={freeMonthsMode}
                            onChange={(checked) => {
                                setFreeMonthsMode(checked);
                                if (checked) {
                                    form.setFieldsValue({ discountPercentage: undefined });
                                } else {
                                    form.setFieldsValue({
                                        freeMonths: undefined,
                                        minInstallments: undefined
                                    });
                                }
                            }}
                        />
                        <span style={{ marginLeft: '8px' }}>{freeMonthsMode ? 'On' : 'Off'}</span>
                    </Form.Item>

                    <Form.Item
                        name="companyID"
                        label="Company"
                        rules={[{ required: true, message: 'Please select a company!' }]}
                    >
                        <Select
                            placeholder="Select a company"
                            showSearch
                            allowClear
                            filterOption={dropDownSearch}
                            optionFilterProp="children"
                            className="company-select"
                            loading={formState.loadingData}
                            dropdownStyle={{
                                maxHeight: '300px',
                                overflow: 'auto',
                                zIndex: 1050,
                                paddingBottom: '8px'
                            }}
                            listHeight={300}
                            onChange={(companyID) => {
                                if (companyID) {
                                    const selectedCompany = formState?.companies?.find((company: any) => company?.id === companyID);
                                    if (selectedCompany && selectedCompany?.maxInstallmentCount) {
                                        form.setFieldsValue({
                                            companyMaxInstallments: Number(selectedCompany?.maxInstallmentCount)
                                        });
                                        setTimeout(() => {
                                            form.validateFields(['minInstallments']).catch(() => { });
                                        }, 100);
                                    }
                                } else {
                                    form.setFieldsValue({
                                        companyMaxInstallments: undefined
                                    });
                                    setTimeout(() => {
                                        form.validateFields(['minInstallments']).catch(() => { });
                                    }, 100);
                                }
                            }}
                        >
                            {formState.companies?.map((company: ICompanyDropDown) => (
                                <Option key={company?.id} value={company?.id}>
                                    {company?.name || 'Unknown Company'}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div
                        className="animated-form-section"
                        style={{
                            maxHeight: !freeMonthsMode ? '200px' : '0',
                            opacity: !freeMonthsMode ? 1 : 0,
                            overflow: 'hidden',
                            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            marginBottom: !freeMonthsMode ? '16px' : '0',
                            transform: !freeMonthsMode ? 'translateY(0)' : 'translateY(-10px)',
                            pointerEvents: !freeMonthsMode ? 'auto' : 'none'
                        }}
                    >
                        <Form.Item
                            name="discountPercentage"
                            label="Discount Percentage"
                            rules={[
                                { required: !freeMonthsMode, message: 'Please enter discount percentage!' },
                                {
                                    pattern: /^(100|[1-9]?\d)$/,
                                    message: 'Discount must be between 0 and 100!'
                                }
                            ]}
                        >
                            <Input
                                placeholder="Enter discount percentage"
                                suffix="%"
                                style={{ width: '100%' }}
                                type="number"
                                min={0}
                                max={100}
                            />
                        </Form.Item>
                    </div>

                    <div
                        className="animated-form-section"
                        style={{
                            maxHeight: freeMonthsMode ? '500px' : '0',
                            opacity: freeMonthsMode ? 1 : 0,
                            overflow: 'hidden',
                            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            marginBottom: freeMonthsMode ? '16px' : '0',
                            transform: freeMonthsMode ? 'translateY(0)' : 'translateY(-10px)',
                            pointerEvents: freeMonthsMode ? 'auto' : 'none'
                        }}
                    >
                        <Form.Item
                            name="companyMaxInstallments"
                            label="Company Maximum Number of Installments"
                        >
                            <InputNumber
                                placeholder="Enter company maximum installments"
                                style={{ width: '100%' }}
                                min={1}
                                max={24}
                                disabled={true}
                            />
                        </Form.Item>
                        <Form.Item
                            name="minInstallments"
                            label="Minimum Number of Installments"
                            dependencies={['freeMonths', 'companyMaxInstallments']}
                            help={warningMessage}
                            rules={[
                                { required: freeMonthsMode, message: 'Please enter minimum number of installments!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!freeMonthsMode) {
                                            return Promise.resolve();
                                        }
                                        if (!value && value !== 0) {
                                            setWarningMessage(null);
                                            return Promise.resolve();
                                        }
                                        
                                        if (value != null && value !== undefined && !Number.isInteger(Number(value))) {
                                            return Promise.reject(new Error('Value must be an integer number.'));
                                        }
                                        
                                        const companyMaxInstallments = getFieldValue('companyMaxInstallments');

                                        if (companyMaxInstallments && value === companyMaxInstallments) {
                                            setWarningMessage(`The minimum number of months is equal to ${companyMaxInstallments} which is the maximum number of installments in the company configuration. i.e the user's request will be totally free.`);
                                        } else {
                                            setWarningMessage(null);
                                        }

                                        // Check  errors
                                        if (companyMaxInstallments && value > companyMaxInstallments) {
                                            setWarningMessage(null);
                                            return Promise.reject(new Error(`The minimum number of installments has to be less than or equal to ${companyMaxInstallments} which is the maximum number of installments in the company's configuration.`));
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <InputNumber
                                placeholder="Enter minimum number of installments"
                                style={{ width: '100%' }}
                                onChange={(value) => {
                                    const companyMaxInstallments = form.getFieldValue('companyMaxInstallments');
                                    if (companyMaxInstallments && value === companyMaxInstallments) {
                                        setWarningMessage(`The minimum number of months is equal to ${companyMaxInstallments} which is the maximum number of installments in the company configuration. i.e the user's request will be totally free.`);
                                    } else {
                                        setWarningMessage(null);
                                    }
                                    setTimeout(() => {
                                        form.validateFields(['minInstallments']).catch(() => { });
                                    }, 0);
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="freeMonths"
                            label="Number of Free Months"
                            dependencies={['minInstallments']}
                            rules={[
                                { required: freeMonthsMode, message: 'Please enter number of free months!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!freeMonthsMode) {
                                            return Promise.resolve();
                                        }
                                        if (!value && value !== 0) {
                                            return Promise.resolve();
                                        }
                                        
                                        if (value != null && value !== undefined && !Number.isInteger(Number(value))) {
                                            return Promise.reject(new Error('Value must be an integer number'));
                                        }
                                        
                                        const minInstallments = getFieldValue('minInstallments');
                                        if (value < 1 || (minInstallments && value > minInstallments)) {
                                            return Promise.reject(new Error('The number of free months can\'t be less than 1 and can\'t be more than the minimum number of installments'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <InputNumber
                                placeholder="Enter number of free months"
                                style={{ width: '100%' }}
                                onChange={() => {
                                    setTimeout(() => {
                                        form.validateFields(['freeMonths']).catch(() => { });
                                    }, 0);
                                }}
                            />
                        </Form.Item>


                    </div>

                    {/* Start Date */}
                    <Form.Item
                        name="startDate"
                        label="Start Date"
                        rules={[{ required: true, message: 'Please select start date!' }]}
                    >
                        <DatePicker
                            placeholder="Select start date"
                            style={{ width: '100%' }}
                            disabledDate={disabledDate}
                        />
                    </Form.Item>

                    {/* End Date */}
                    <Form.Item
                        name="endDate"
                        label="End Date"
                        rules={[
                            { required: true, message: 'Please select end date!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const startDate = getFieldValue('startDate');
                                    if (!value || !startDate || value?.isAfter(startDate)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('End date must be after start date!'));
                                },
                            }),
                        ]}
                    >
                        <DatePicker
                            placeholder="Select end date"
                            style={{ width: '100%' }}
                            disabledDate={disabledDate}
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxAllowedRequests"
                        label={
                            <div className="label-with-icon">
                                Max Allowed Number of Requests
                                <Tooltip title="Leave this field empty if you don't want to have a limit for the number of requests.">
                                    <InfoCircleOutlined className="info-icon" />
                                </Tooltip>
                            </div>
                        }
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || value === '') {
                                        return Promise.resolve();
                                    }
                                    if (isNaN(value) || Number(value) < 1) {
                                        return Promise.reject(new Error('Must be at least 1!'));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <Input
                            placeholder="Enter max requests (optional)"
                            type="number"
                            min={1}
                            style={{ width: '100%' }}
                            className="max-requests-input"
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxAllowedAmount"
                        label={
                            <div className="label-with-icon">
                                Max Allowed Amount
                                <Tooltip title="Leave this field empty if you don't want to have a limit on the total requests' amounts.">
                                    <InfoCircleOutlined className="info-icon" />
                                </Tooltip>
                            </div>
                        }
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || value === '') {
                                        return Promise.resolve();
                                    }
                                    if (isNaN(value) || Number(value) < 0) {
                                        return Promise.reject(new Error('Amount must be positive!'));
                                    }
                                    if (Number(value) > 100000) {
                                        return Promise.reject(new Error('Amount cannot exceed 100,000 SAR!'));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >

                        <Input
                            suffix="SAR"
                            placeholder="Enter max amount (optional)"
                            type="number"
                            min={0}
                            max={100000}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    {/* Services */}
                    <Form.Item
                        name="services"
                        label="Services"
                    // rules={[{ required: true, message: 'Please select at least one service!' }]}
                    >
                        <Select
                            placeholder="Select services"
                            showSearch
                            allowClear
                            filterOption={dropDownSearch}
                            optionFilterProp="children"
                            style={{ width: '100%' }}
                            className="services-select"
                            loading={formState.loadingData}
                            dropdownStyle={{
                                maxHeight: '300px',
                                overflow: 'auto',
                                zIndex: 1050,
                                paddingBottom: '8px'
                            }}
                            listHeight={300}
                            disabled={true}
                        >
                            {formState.services?.map((service: IService) => (
                                <Option key={service?.id} value={service?.id}>
                                    {service?.name || 'Unknown Service'}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div className="checkbox-section">
                        <Form.Item
                            name="activateUponFirstRequest"
                            valuePropName="checked"
                        >
                            <Checkbox className="custom-checkbox">
                                <span className="checkbox-text">Activate Upon First Request</span>
                            </Checkbox>
                        </Form.Item>
                    </div>

                    <div className="action-buttons">
                        <Button
                            onClick={onReset}
                            size="large"
                            className="reset-button"
                        >
                            Reset
                        </Button>

                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={formState.loading}
                            size="large"
                            className="submit-button"
                        >
                            Create Offer
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}; 